import { getFontStack } from '../../config';
import { normalizeRuns, resolveRunStyle } from './textRuns';

const STYLE_ATTR = 'data-ms-style';
const FILLER_ATTR = 'data-ms-filler';

const styleToCss = (style, zoom) => {
  const decorations = [style.underline ? 'underline' : null, style.strike ? 'line-through' : null].filter(Boolean);
  return {
    fontFamily: getFontStack(style.fontFamily),
    fontSize: `${style.fontSize * zoom}px`,
    color: style.fill,
    fontWeight: style.bold ? '700' : '400',
    fontStyle: style.italic ? 'italic' : 'normal',
    textDecoration: decorations.length ? decorations.join(' ') : 'none',
  };
};

export const renderRunsToDom = (container, runs, blockStyle, zoom) => {
  container.replaceChildren();
  runs.forEach((run) => {
    if (!run.text) {
      return;
    }
    const span = document.createElement('span');
    span.setAttribute(STYLE_ATTR, JSON.stringify(run.style || {}));
    Object.assign(span.style, styleToCss(resolveRunStyle(blockStyle, run.style), zoom));
    run.text.split('\n').forEach((part, index) => {
      if (index > 0) {
        span.appendChild(document.createElement('br'));
      }
      if (part) {
        span.appendChild(document.createTextNode(part));
      }
    });
    container.appendChild(span);
  });

  const text = runs.map((run) => run.text).join('');
  if (!text || text.endsWith('\n')) {
    const filler = document.createElement('br');
    filler.setAttribute(FILLER_ATTR, '');
    container.appendChild(filler);
  }
};

const readStyleAttr = (node, inherited) => {
  const raw = node.getAttribute?.(STYLE_ATTR);
  if (raw == null) {
    return inherited;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return inherited;
  }
};

const collectSegments = (root) => {
  const segments = [];
  const lastText = () => (segments.length ? segments[segments.length - 1].text : '\n');

  const walk = (node, style) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.data) {
        segments.push({ node, text: node.data.replace(/\u00a0/g, ' '), style });
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
      return;
    }
    const tag = node.tagName?.toLowerCase();
    if (tag === 'br') {
      if (!node.hasAttribute(FILLER_ATTR)) {
        segments.push({ node, text: '\n', style });
      }
      return;
    }
    const isBlock = tag === 'div' || tag === 'p';
    if (isBlock && segments.length && !lastText().endsWith('\n')) {
      segments.push({ node, text: '\n', style, virtual: true });
    }
    const nextStyle = readStyleAttr(node, style);
    Array.from(node.childNodes).forEach((child) => walk(child, nextStyle));
  };

  Array.from(root.childNodes).forEach((child) => walk(child, {}));
  return segments;
};

export const readRunsFromDom = (container) =>
  normalizeRuns(collectSegments(container).map((segment) => ({ text: segment.text, style: segment.style })));

const textLengthBefore = (container, node, offset) => {
  const range = document.createRange();
  range.setStart(container, 0);
  range.setEnd(node, offset);
  return collectSegments(range.cloneContents()).reduce((sum, segment) => sum + segment.text.length, 0);
};

export const getSelectionOffsets = (container) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
    return null;
  }
  const start = textLengthBefore(container, range.startContainer, range.startOffset);
  const end = textLengthBefore(container, range.endContainer, range.endOffset);
  return { start: Math.min(start, end), end: Math.max(start, end) };
};

const pointAt = (container, index) => {
  let position = 0;
  const segments = collectSegments(container).filter((segment) => !segment.virtual);
  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const length = segment.text.length;
    if (segment.node.nodeType === Node.TEXT_NODE && index <= position + length) {
      return { node: segment.node, offset: index - position };
    }
    if (segment.node.nodeType !== Node.TEXT_NODE && index <= position) {
      const parent = segment.node.parentNode;
      return { node: parent, offset: Array.from(parent.childNodes).indexOf(segment.node) };
    }
    position += length;
  }
  const filler = container.querySelector(`br[${FILLER_ATTR}]`);
  if (filler) {
    return { node: container, offset: Array.from(container.childNodes).indexOf(filler) };
  }
  return { node: container, offset: container.childNodes.length };
};

export const setSelectionOffsets = (container, start, end) => {
  const selection = window.getSelection();
  if (!selection) {
    return;
  }
  const from = pointAt(container, start);
  const to = pointAt(container, end);
  const range = document.createRange();
  range.setStart(from.node, from.offset);
  range.setEnd(to.node, to.offset);
  selection.removeAllRanges();
  selection.addRange(range);
};
